/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {AdobeMetricsRuntime, FetchFn} from './AdobeMetricsRuntime';
import {Level, Metric, RecordType} from '@exc/metrics';
import {METRICS_VERSION as sdkVersion} from './BuildVersion';
import uuidv1 from 'uuid/v1';

const LOGGER_NAME = 'exc.metrics-browser-runtime.network';

/**
 * The serializeNonBodyProperties function converts an object that was
 * provided to the network overload into a representation for ADX.
 * Currently, it removes body-related properties.
 *
 * @param {any} obj The Request object to serialize.
 * @returns {object} A property bag.
 */
function serializeNonBodyProperties(obj: any): object {
  const requestKeys = ['cache', 'context', 'credentials', 'destination', 'headers', 'integrity',
    'method', 'mode', 'redirect', 'referrer', 'referrerPolicy', 'url'];
  let arg: any;
  if (obj instanceof URL) {
    arg = obj.toString();
  } else if (obj instanceof Request) {
    arg = {};
    const o = obj as any;
    requestKeys.forEach(key => {o[key] && (arg[key] = o[key]);});
  } else {
    arg = Object.assign({}, obj) as any;
  }

  // don't record any body properties
  for (const key of Object.keys(arg as any)) {
    if (key.match(/body/gi)) {
      delete arg[key];
    }
  }

  return arg;
}

function instrumentRequest(adobeMetrics: AdobeMetricsRuntime, dateNow: number, correlationId: string, ...args: any): Promise<any> {
  const m = {
    data: [...args],
    dateNow,
    level: Level.INFO,
    metricsState: {
      correlationId,
      sdkVersion
    },
    name: LOGGER_NAME,
    recordType: RecordType.NETWORK_REQUEST
  } as Metric;

  // check if we need special handling on arg[0] and  arg[1]
  for (let i = 0; i < Math.max(m.data.length, 2); ++i) {
    if (m.data[i] && typeof m.data[i] !== 'string') {
      m.data[i] = serializeNonBodyProperties(m.data[i]);
    }
  }
  adobeMetrics.write(m);
  adobeMetrics.connections += 1;
  return Promise.resolve(args);
}

function instrumentResponse(adobeMetrics: AdobeMetricsRuntime, start: number, correlationId: string, response: any, params: any): Promise<any> {
  const now = Date.now();
  const m = {
    data: Object.assign({}, response, {
      headers: {},
      ok: response.ok,
      redirected: response.redirected,
      status: response.status,
      statusText: response.statusText,
      type: response.type,
      url: response.url,
      useFinalURL: (response as any).useFinalURL
    }),
    dateNow: now,
    duration: now - start,
    event: String(response.status),
    level: Level.INFO,
    message: response.statusText,
    metricsState: {
      correlationId,
      sdkVersion
    },
    name: LOGGER_NAME,
    recordType: RecordType.NETWORK_RESPONSE
  } as Metric;

  for (const pair of response.headers.entries()) {
    (m.data as any).headers[pair[0]] = pair[1];
  }
  adobeMetrics.write(m, {params, response});
  adobeMetrics.connections -= 1;
  return Promise.resolve(response);
}

function instrumentResponseError(
  adobeMetrics: AdobeMetricsRuntime,
  start: number,
  correlationId: string,
  url: string,
  fetchArgs: any[],
  error: any
): Promise<any> {
  const now = Date.now();
  const m = {
    data: Object.assign({}, error, {
      description: error.description,
      fetchArgs,
      fileName: error.fileName,
      lineNumber: error.lineNumber,
      message: error.message,
      name: error.name,
      number: error.number,
      stack: error.stack,
      url
    }),
    dateNow: now,
    duration: now - start,
    event: 'Error',
    level: Level.ERROR,
    message: `${error}`,
    metricsState: {
      correlationId,
      sdkVersion
    },
    name: LOGGER_NAME,
    recordType: RecordType.NETWORK_RESPONSE
  } as Metric;
  adobeMetrics.write(m);
  adobeMetrics.connections -= 1;
  return Promise.reject(error);
}

function invoke(fetch: FetchFn, adobeMetrics: AdobeMetricsRuntime, ...args: any): Promise<any> {
  const correlationId = uuidv1();
  const url = args[0];
  const params = args.slice(1);
  const start = Date.now();
  return Promise.resolve(args)
    .then((args): any => instrumentRequest(adobeMetrics, start, correlationId, ...args))
    // @ts-expect-error TS2556
    .then((args): any => fetch(...args))
    .then((response): any => instrumentResponse(adobeMetrics, start, correlationId, response, params),
      (error): any => instrumentResponseError(adobeMetrics, start, correlationId, url, params, error));
}

function wrap(fetchFn: FetchFn, adobeMetrics: AdobeMetricsRuntime): FetchFn {
  return function(...args: any): Promise<any> {
    return invoke(fetchFn, adobeMetrics, ...args);
  };
}

/**
 * Overload/intercept the XMLHttpRequest methods open, send, and setRequestHeader.
 */

function interceptXHR(xhr: XMLHttpRequest, adobeMetrics: AdobeMetricsRuntime): void {
  let uri: string;
  const fetchData: Record<string, string | object> = {};
  const requestHeaders: Record<string, string | object> = {};

  function processResponseHeaders(xhr: XMLHttpRequest): Map<string, string> {
    const headers = xhr.getAllResponseHeaders();
    const arr = headers.trim().split(/[\r\n]+/);

    // Create a map of header names to values
    const headerMap = new Map<string, string>();
    arr.forEach(function (line: string) {
      const parts = line.split(': ');
      const header = parts.shift();
      const value = parts.join(': ');
      if (header) {
        headerMap.set(header, value);
      }
    });
    return headerMap;
  }

  // Overload/intercept XHR open()
  const open = xhr.open;
  xhr.open = function(this: XMLHttpRequest, method: string, url: string, async?: boolean, username?: string, password?: string): void {
    uri = url;
    if (async !== false) {
      async = true;
    }
    fetchData['method'] = method;
    open.apply(this, [method, url, async, username, password]);
  };

  // Overload/intercept XHR setRequestHeader()
  // request headers will be set between open() and send(), if at all
  const setRequestHeader = xhr.setRequestHeader;
  xhr.setRequestHeader = function(this: XMLHttpRequest, name: string, value: string): void {
    requestHeaders[name] = value;
    setRequestHeader.apply(this, [name, value]);
  };

  // Overload/intercept XHR send()
  const send = xhr.send;
  xhr.send = function(this: XMLHttpRequest, body?: any): void {
    const correlationId = uuidv1();
    const start = Date.now();

    // error handler
    function handleError(): void {
      /* instrumentResponseError returns a Promise rejection, so we must handle the "error" even
         though we don't do anything with it to avoid an unhandled promise rejection warning */
      instrumentResponseError(adobeMetrics, start, correlationId, uri, [fetchData], TypeError('Network request failed'))
        .then(r => r, e => e);
    }

    // response handler
    function handleResponse(e: any): void {
      const headerMap = processResponseHeaders(e.target);
      const res = {};
      const status: number = e.target.status;
      const ok: boolean = 200 <= status && status <= 299;
      // "" responseType is same as default of 'text'
      const type: string = e.target.responseType === '' ? 'text' :  e.target.responseType;
      Object.assign(res, e.target, {
        headers: headerMap,
        ok,
        status,
        statusText: e.target.statusText,
        type,
        url: e.target.responseURL
      });

      instrumentResponse(adobeMetrics, start, correlationId, res, {headers: requestHeaders}).then();
    }

    // requestHeaders comes from overloaded setRequestHeaders function,
    // which would be called between open() and send(), if at all
    if (Object.keys(requestHeaders).length > 0) {
      fetchData['headers'] = requestHeaders;
    }

    instrumentRequest(adobeMetrics, start, correlationId, uri, fetchData).then();

    this.addEventListener('load', handleResponse);
    this.addEventListener('error', handleError);

    send.apply(this, [body]);
  };
}

/**
 * Initialize the Network interceptor. This overloads/intercepts
 * (window|global).fetch and XMLHttpRequest's open, send, and
 * setRequestHeader functions.
 *
 * @param {AdobeMetricsRuntime} adobeMetrics Adobe Metrics object.
 * @param {typeof global} g The global object to intercept.
 * @param {string} mode Set explicitly to 'off' to disable the overload,
 * or set any other value including undefined to enable the overload.
 */
function init(adobeMetrics: AdobeMetricsRuntime, g: Window & typeof globalThis, mode?: string): void {
  const xmlPrototype = g.XMLHttpRequest?.prototype;
  let nativeFetch;
  switch (mode) {
    case 'off':
      if (g._originalFetch) {
        g.fetch = g._originalFetch;
        delete g._originalFetch;
      }
      if (g._XHRintercept && xmlPrototype) {
        xmlPrototype.open = xmlPrototype._open as typeof xmlPrototype.open;
        delete xmlPrototype._open;
        xmlPrototype.send = xmlPrototype._send as typeof xmlPrototype.send;
        delete xmlPrototype._send;
        xmlPrototype.setRequestHeader = xmlPrototype._setRequestHeader as typeof xmlPrototype.setRequestHeader;
        delete xmlPrototype._setRequestHeader;
        g._XHRintercept = false;
      }
      break;
    default:
      // If fetch is not native (i.e. is a polyfill) then it is likely using XHR under the hood
      // (like the GitHub/fetch plugin). In this case, we don't want to intercept XHR because
      // it will lead to double instrumentation or worse (e.g. memory leaks)
      // We must keep the fetch overload for continuity of existing data collection
      if (g.fetch) {
        nativeFetch = /{\s+\[native code]/.test(Function.prototype.toString.call(g.fetch));
        if (!g._originalFetch) {
          g._originalFetch = g.fetch;
          g.fetch = wrap(g.fetch, adobeMetrics);
        }
      }
      if (!g._XHRintercept && xmlPrototype) {
        g._XHRintercept = true;
        xmlPrototype._open = xmlPrototype.open;
        xmlPrototype._send = xmlPrototype.send;
        xmlPrototype._setRequestHeader = xmlPrototype.setRequestHeader;
        // again, only intercept XHR if fetch is native (likely)
        if (nativeFetch) {
          interceptXHR(g.XMLHttpRequest.prototype, adobeMetrics);
        }
      }
      break;
  }
}

const MetricsNetworkIntercept = {
  init,
  instrumentRequest,
  instrumentResponse,
  instrumentResponseError
};
export default MetricsNetworkIntercept;
